// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";
import React, { Children, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    includeActions?: boolean;
    isCondensed?: boolean;
    [key: string]: any;
}
type SubComponentWithChildren = React.FC<{ children?: any }> & { children?: any };

interface OwnProps {
    Error: SubComponentWithChildren;
    Image: SubComponentWithChildren;
    Description: SubComponentWithChildren;
    Cases: SubComponentWithChildren;
    Price: SubComponentWithChildren;
    Qty: SubComponentWithChildren;
    Total: SubComponentWithChildren;
    Remove: SubComponentWithChildren;
    Actions: SubComponentWithChildren;
    Notes: SubComponentWithChildren;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    error?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    cases?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    remove?: InjectableCss;
    notes?: InjectableCss;
    infoSmallScreen?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

const enum NAMES {
    Error = "Error",
    Image = "Image",
    Description = "Description",
    Cases = "Cases",
    Price = "Price",
    Qty = "Qty",
    Total = "Total",
    Remove = "Remove",
    Actions = "Actions",
    Notes = "Notes",
}

const Error: SubComponentWithChildren = () => null;
const Image: SubComponentWithChildren = () => null;
const Description: SubComponentWithChildren = () => null;
const Cases: SubComponentWithChildren = () => null;
const Price: SubComponentWithChildren = () => null;
const Qty: SubComponentWithChildren = () => null;
const Total: SubComponentWithChildren = () => null;
const Remove: SubComponentWithChildren = () => null;
const Actions: SubComponentWithChildren = () => null;
const Notes: SubComponentWithChildren = () => null;

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Error),
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Description),
        getChild(childrenArray, NAMES.Cases),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Qty),
        getChild(childrenArray, NAMES.Total),
        getChild(childrenArray, NAMES.Remove),
        getChild(childrenArray, NAMES.Actions),
        getChild(childrenArray, NAMES.Notes),
    ] as { props?: SubComponentWithChildren }[];
};

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    includeActions = false,
    isCondensed = false,
    ...props
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const [Error, Image, Description, Cases, Price, Qty, Total, Remove, Actions, Notes] = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement[];
        return getDestructeredChildren(childrenArray);
    }, [children]);

    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    return (
        <StyledWrapper {...styles.container} {...{ includeActions, isCondensed }} {...props}>
            <StyledWrapper {...styles.error}>{Error?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.image}>{Image?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.description} {...{ isCondensed }}>
                {Description?.props?.children}
            </StyledWrapper>
            {isTabletScreen ? (
                <StyledWrapper {...styles.infoSmallScreen}>
                    <StyledWrapper {...styles.cases} className="cases-column">
                        {Cases?.props?.children}
                    </StyledWrapper>
                    <StyledWrapper {...styles.price} className="price-column">
                        {Price?.props?.children}
                    </StyledWrapper>
                    <StyledWrapper {...styles.qty}>{Qty?.props?.children}</StyledWrapper>
                    <StyledWrapper {...styles.total}>{Total?.props?.children}</StyledWrapper>
                </StyledWrapper>
            ) : (
                <>
                    <StyledWrapper {...styles.cases} className="cases-column">
                        {Cases?.props?.children}
                    </StyledWrapper>
                    <StyledWrapper {...styles.price} className="price-column">
                        {Price?.props?.children}
                    </StyledWrapper>
                    <StyledWrapper {...styles.qty}>{Qty?.props?.children}</StyledWrapper>
                    <StyledWrapper {...styles.total}>{Total?.props?.children}</StyledWrapper>
                </>
            )}

            <StyledWrapper {...styles.remove} className="remove-column">
                {Remove?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.actions} {...{ isCondensed }}>
                {Actions?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.notes} className="notes-section">
                {Notes?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Error = Error;
CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Cases = Cases;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;

CartLineContainer.Error.displayName = NAMES.Error;
CartLineContainer.Image.displayName = NAMES.Image;
CartLineContainer.Description.displayName = NAMES.Description;
CartLineContainer.Cases.displayName = NAMES.Cases;
CartLineContainer.Price.displayName = NAMES.Price;
CartLineContainer.Qty.displayName = NAMES.Qty;
CartLineContainer.Total.displayName = NAMES.Total;
CartLineContainer.Remove.displayName = NAMES.Remove;
CartLineContainer.Actions.displayName = NAMES.Actions;
CartLineContainer.Notes.displayName = NAMES.Notes;

export default CartLineContainer;
